import { useState } from 'react';
import { Plus, Trash2, FileText, Search } from 'lucide-react';
import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { ScrollArea } from '@/components/ui/scroll-area.js';
import { cn } from '@/lib/utils.js';
import { useAppContext, type NoteView } from '@/contexts/AppContext.js';
import { useArtifactPanelStore } from '@/stores/artifactPanelStore.js';

export function NotesListArtifact() {
  const [searchQuery, setSearchQuery] = useState('');
  const { notes, createNewNote, deleteNote, openNote } = useAppContext();
  const { openTab, openTabs } = useArtifactPanelStore();

  const filteredNotes = notes.filter(
    (note) =>
      note.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      note.content.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleCreateNote = () => {
    createNewNote();
    // The newly created note will be set as currentNote in context
    // We need to open it as a tab
    const newNoteId = `note-${Date.now()}`;
    openTab({
      id: newNoteId,
      type: 'note',
      title: 'Untitled Note',
      data: { noteId: newNoteId },
    });
  };

  const handleSelectNote = (note: NoteView) => {
    openNote(note);
    openTab({
      id: `note-${note.id}`,
      type: 'note',
      title: note.title || 'Untitled Note',
      data: { noteId: note.id },
    });
  };

  const isNoteOpen = (noteId: string) => {
    return openTabs.includes(`note-${noteId}`);
  };

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (days === 1) {
      return 'Yesterday';
    } else if (days < 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="flex-shrink-0 px-4 py-3 border-b border-border/50">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            <h2 className="font-semibold">Notes</h2>
            <span className="text-xs text-muted-foreground">({notes.length})</span>
          </div>
          <Button size="sm" onClick={handleCreateNote} className="gap-1.5 h-8">
            <Plus className="h-3.5 w-3.5" />
            New
          </Button>
        </div>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search notes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-8 h-9"
          />
        </div>
      </div>

      {/* Notes List */}
      <ScrollArea className="flex-1">
        <div className="p-2 space-y-1">
          {filteredNotes.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-sm text-muted-foreground">
                {searchQuery ? 'No notes found' : 'No notes yet'}
              </p>
              {!searchQuery && (
                <Button
                  variant="link"
                  size="sm"
                  onClick={handleCreateNote}
                  className="mt-2"
                >
                  Create your first note
                </Button>
              )}
            </div>
          ) : (
            filteredNotes.map((note) => (
              <div
                key={note.id}
                className={cn(
                  'group flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-colors',
                  'hover:bg-muted/60',
                  isNoteOpen(note.id) && 'bg-muted/40'
                )}
                onClick={() => handleSelectNote(note)}
              >
                <div className="flex-shrink-0 mt-0.5">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-sm truncate">
                    {note.title || 'Untitled Note'}
                  </h3>
                  {note.content && (
                    <p className="text-xs text-muted-foreground truncate mt-0.5">
                      {note.content.slice(0, 60)}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    {formatDate(note.updatedAt)}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteNote(note.id);
                  }}
                >
                  <Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive" />
                </Button>
              </div>
            ))
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
